import {
  AdminAnalyticsPreferencesBlob,
  PlatformKpiKey,
} from "@/types/admin-analytics";

/**
 * KPI card ids stored in the (frontend-owned) preferences blob.
 * One card per dashboard widget — funnel cards combine their three KPI keys.
 */
export const PLATFORM_KPI_CARD_IDS = [
  "preCheckIn",
  "preCheckOut",
  "offers",
  "offerRevenue",
  "moduleUsage",
  "uniqueActiveGuests",
] as const;

export type PlatformKpiCardId = (typeof PLATFORM_KPI_CARD_IDS)[number];

export const PLATFORM_KPI_CARD_TITLE_KEYS: Record<PlatformKpiCardId, string> = {
  preCheckIn: "analytics.platform.kpi.preCheckIn",
  preCheckOut: "analytics.platform.kpi.preCheckOut",
  offers: "analytics.platform.kpi.offers",
  offerRevenue: "analytics.platform.kpi.offerRevenue",
  moduleUsage: "analytics.platform.kpi.moduleUsageTotal",
  uniqueActiveGuests: "analytics.platform.kpi.uniqueActiveGuests",
};

/** Delta key shown on each card in compare mode. */
export const PLATFORM_KPI_CARD_DELTA_KEYS: Record<
  PlatformKpiCardId,
  PlatformKpiKey
> = {
  preCheckIn: "preCheckInCompleted",
  preCheckOut: "preCheckOutCompleted",
  offers: "offersPurchased",
  offerRevenue: "offerRevenueCents",
  moduleUsage: "moduleUsageTotal",
  uniqueActiveGuests: "uniqueActiveGuests",
};

const isCardId = (value: unknown): value is PlatformKpiCardId =>
  typeof value === "string" &&
  (PLATFORM_KPI_CARD_IDS as readonly string[]).includes(value);

/** Saved order first (unknown ids dropped), then any cards missing from the blob. */
export const resolveCardOrder = (
  blob?: AdminAnalyticsPreferencesBlob,
): PlatformKpiCardId[] => {
  const saved = Array.isArray(blob?.order) ? blob.order.filter(isCardId) : [];
  const missing = PLATFORM_KPI_CARD_IDS.filter((id) => !saved.includes(id));
  return [...saved, ...missing];
};

/** Falls back to "all visible" when nothing valid is saved. */
export const resolveVisibleCards = (
  blob?: AdminAnalyticsPreferencesBlob,
): Set<PlatformKpiCardId> => {
  if (!Array.isArray(blob?.visibleMetrics)) {
    return new Set(PLATFORM_KPI_CARD_IDS);
  }
  const visible = new Set(blob.visibleMetrics.filter(isCardId));
  return visible.size > 0 ? visible : new Set(PLATFORM_KPI_CARD_IDS);
};
